import React, { useState, useEffect } from 'react';
import {
   Box,
   Paper,
   Typography,
   Button,
   CircularProgress,
   Alert,
   Card,
   CardContent,
   Grid,
   List,
   ListItem,
   ListItemText,
   ListItemAvatar,
   Avatar,
   IconButton,
   Chip,
   Divider,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   TextField,
   useTheme,
   useMediaQuery,
} from '@mui/material';
import {
   QrCode as QrCodeIcon,
   Share as ShareIcon,
   Visibility as VisibilityIcon,
   Chat as ChatIcon,
   Person as PersonIcon,
   Close as CloseIcon,
   Send as SendIcon,
   Download as DownloadIcon,
   Business as BusinessIcon,
   FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import { useUser } from '../../../contexts/UserContext';
import { useWebSocketChat } from '../../../contexts/WebSocketChatContext';
import { getAuthHeaders } from '../../../utils/auth';
import webSocketService from '../../../services/WebSocketService';
import {
   doc,
   setDoc,
   getDoc,
   collection,
   query,
   where,
   orderBy,
   onSnapshot,
   addDoc,
   serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useNavigate } from 'react-router-dom';

// Using require syntax to avoid ES module import issues
const QRCode = require('qrcode.react').QRCodeSVG;

interface QRScanData {
   id: string;
   scannedBy: string;
   scannerName: string;
   scannerEmail?: string;
   scannerPhotoURL?: string;
   scannedAt: any;
   signedUp: boolean;
   signupDate?: any;
   source?: 'business_card' | 'profile_qr' | 'profile_link';
}

interface ProfileQRCodeProps {
   userId: string;
}

const ProfileQRCode: React.FC<ProfileQRCodeProps> = ({ userId }) => {
   const { currentUser } = useUser();
   const { createOneOnOneChat } = useWebSocketChat();
   const navigate = useNavigate();
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [qrUrl, setQrUrl] = useState<string>('');
   const [totalScans, setTotalScans] = useState(0);
   const [scanHistory, setScanHistory] = useState<QRScanData[]>([]);
   const [shareDialogOpen, setShareDialogOpen] = useState(false);
   const [downloading, setDownloading] = useState<string | null>(null);

   // Generate unique QR code URL for the user with referral token
   useEffect(() => {
      const setupQRCode = async () => {
         try {
            // Create or get the QR code tracking document
            const qrTrackingRef = doc(db, 'user_qr_codes', userId);
            const qrDoc = await getDoc(qrTrackingRef);

            if (!qrDoc.exists()) {
               // Initialize tracking document if it doesn't exist
               await setDoc(qrTrackingRef, {
                  userId,
                  totalScans: 0,
                  created: serverTimestamp(),
                  lastScan: null,
               });
            }

            // Generate the URL for the QR code with source parameter
            // The userId in the URL path acts as the referrerId
            const qrShareUrl = `${window.location.origin}/qr/${userId}?source=profile_qr`;
            setQrUrl(qrShareUrl);
            setLoading(false);
         } catch (err) {
            console.error('Error setting up QR code:', err);
            setError('Failed to generate QR code');
            setLoading(false);
         }
      };

      setupQRCode();
   }, [userId]);

   // Listen to scan history and total scans
   useEffect(() => {
      if (!userId) return;

      const qrTrackingRef = doc(db, 'user_qr_codes', userId);
      const scanHistoryRef = collection(db, 'user_qr_codes', userId, 'scans');
      const scanQuery = query(scanHistoryRef, orderBy('scannedAt', 'desc'));

      // Listen to total scans
      const unsubscribeTracking = onSnapshot(qrTrackingRef, (doc) => {
         if (doc.exists()) {
            setTotalScans(doc.data().totalScans || 0);
         }
      });

      // Listen to scan history
      const unsubscribeScans = onSnapshot(scanQuery, (snapshot) => {
         const scans: QRScanData[] = [];
         snapshot.forEach((doc) => {
            scans.push({ id: doc.id, ...doc.data() } as QRScanData);
         });
         setScanHistory(scans);
      });

      return () => {
         unsubscribeTracking();
         unsubscribeScans();
      };
   }, [userId]);

   const handleShareQR = () => {
      setShareDialogOpen(true);
   };

   const handleCopyLink = () => {
      navigator.clipboard.writeText(qrUrl);
      // You might want to show a toast notification here
   };

   const handleDownloadBusinessCard = async () => {
      if (!userId) return;

      setDownloading('business-card');
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4001'}/api/profile/${userId}/business-card`, {
            method: 'GET',
            headers,
         });

         if (!response.ok) {
            throw new Error('Failed to download business card');
         }

         const blob = await response.blob();
         const url = window.URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.href = url;
         link.download = `${currentUser?.displayName || 'Business Card'}.pdf`;
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         window.URL.revokeObjectURL(url);
      } catch (error) {
         console.error('Error downloading business card:', error);
         setError('Failed to download business card');
      } finally {
         setDownloading(null);
      }
   };

   const handleDownloadQRCode = async () => {
      if (!userId) return;

      setDownloading('qr-code');
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4001'}/api/profile/${userId}/qr-code-pdf`, {
            method: 'GET',
            headers,
         });

         if (!response.ok) {
            throw new Error('Failed to generate QR code PDF');
         }

         const blob = await response.blob();
         const url = window.URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.href = url;
         link.download = `${currentUser?.displayName || 'QR'}-Code.pdf`;
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         window.URL.revokeObjectURL(url);
      } catch (error) {
         console.error('Error downloading QR code:', error);
         setError('Failed to download QR code');
      } finally {
         setDownloading(null);
      }
   };

   const handleDownloadProfile = async () => {
      if (!userId) return;

      setDownloading('profile');
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4001'}/api/profile/${userId}/profile-pdf`, {
            method: 'GET',
            headers,
         });

         if (!response.ok) {
            throw new Error('Failed to download profile');
         }

         const blob = await response.blob();
         const url = window.URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.href = url;
         link.download = `${currentUser?.displayName || 'Profile'}_Profile.pdf`;
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         window.URL.revokeObjectURL(url);
      } catch (error) {
         console.error('Error downloading profile:', error);
         setError('Failed to download profile');
      } finally {
         setDownloading(null);
      }
   };

   const [contactModalOpen, setContactModalOpen] = useState(false);
   const [selectedContact, setSelectedContact] = useState<QRScanData | null>(null);
   const [messageText, setMessageText] = useState('');
   const [sendingMessage, setSendingMessage] = useState(false);

   const handleStartChat = async (scanData: QRScanData) => {
      setSelectedContact(scanData);
      setContactModalOpen(true);
   };

   const handleCloseContactModal = () => {
      if (!sendingMessage) {
         setContactModalOpen(false);
         setMessageText('');
         setSelectedContact(null);
      }
   };

   const handleSendMessage = async () => {
      if (!selectedContact || !messageText.trim() || sendingMessage) return;

      setSendingMessage(true);
      try {
         // Create or get existing one-on-one chat
         const chatRoom = await createOneOnOneChat(selectedContact.scannedBy);

         if (chatRoom) {
            // Send the message using WebSocket service
            await webSocketService.sendMessage(chatRoom.id, messageText.trim());

            // Close modal and reset
            handleCloseContactModal();

            // Message sent successfully, but don't redirect to chat page
            console.log('Message sent successfully');
         } else {
            console.error('Failed to create chat room');
         }
      } catch (error) {
         console.error('Error sending message:', error);
      } finally {
         setSendingMessage(false);
      }
   };

   if (loading) {
      return (
         <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
         </Box>
      );
   }

   if (error) {
      return (
         <Box sx={{ p: 3 }}>
            <Alert severity='error'>{error}</Alert>
         </Box>
      );
   }

   return (
      <Box sx={{ p: isMobile ? 2 : 3 }}>
         <Grid container spacing={3}>
            {/* QR Code Section */}
            <Grid item xs={12} md={6}>
               <Card sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                     <Typography
                        variant='h6'
                        gutterBottom
                        sx={{
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           gap: 1,
                        }}
                     >
                        <QrCodeIcon />
                        Your QR Code
                     </Typography>
                     <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
                        Share this QR code to let others connect with you and join the platform
                     </Typography>

                     <Box
                        id='profile-qr-code'
                        sx={{
                           display: 'flex',
                           justifyContent: 'center',
                           mb: 3,
                           p: 2,
                           bgcolor: 'white',
                           borderRadius: 2,
                           border: '1px solid #e0e0e0',
                        }}
                     >
                        <QRCode
                           value={qrUrl}
                           size={isMobile ? 200 : 250}
                           level='H'
                           fgColor='#000000'
                           bgColor='#ffffff'
                           includeMargin={true}
                        />
                     </Box>

                     <Box
                        sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}
                     >
                        <Button
                           variant='contained'
                           startIcon={<ShareIcon />}
                           onClick={handleShareQR}
                           sx={{ minWidth: 120 }}
                        >
                           Share
                        </Button>

                        <Button
                           variant='outlined'
                           startIcon={
                              downloading === 'business-card' ? (
                                 <CircularProgress size={16} />
                              ) : (
                                 <BusinessIcon />
                              )
                           }
                           onClick={handleDownloadBusinessCard}
                           disabled={downloading === 'business-card'}
                           sx={{ minWidth: 160 }}
                        >
                           {downloading === 'business-card' ? 'Generating...' : 'Business Card'}
                        </Button>

                        <Button
                           variant='outlined'
                           startIcon={
                              downloading === 'qr-code' ? (
                                 <CircularProgress size={16} />
                              ) : (
                                 <QrCodeIcon />
                              )
                           }
                           onClick={handleDownloadQRCode}
                           disabled={downloading === 'qr-code'}
                           sx={{ minWidth: 140 }}
                        >
                           {downloading === 'qr-code' ? 'Generating...' : 'QR Code PDF'}
                        </Button>

                        <Button
                           variant='outlined'
                           startIcon={
                              downloading === 'profile' ? (
                                 <CircularProgress size={16} />
                              ) : (
                                 <FileDownloadIcon />
                              )
                           }
                           onClick={handleDownloadProfile}
                           disabled={downloading === 'profile'}
                           sx={{ minWidth: 140 }}
                        >
                           {downloading === 'profile' ? 'Downloading...' : 'Download Profile'}
                        </Button>
                     </Box>
                  </CardContent>
               </Card>
            </Grid>

            {/* Statistics Section */}
            <Grid item xs={12} md={6}>
               <Card sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                     <Typography
                        variant='h6'
                        gutterBottom
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                     >
                        <VisibilityIcon />
                        QR Code Statistics
                     </Typography>

                     <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography variant='h3' color='primary' sx={{ fontWeight: 'bold' }}>
                           {totalScans}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                           Total Scans
                        </Typography>
                     </Box>

                     <Divider sx={{ mb: 2 }} />

                     <Typography variant='subtitle1' gutterBottom>
                        Recent Activity
                     </Typography>

                     {scanHistory.length === 0 ? (
                        <Typography
                           variant='body2'
                           color='text.secondary'
                           sx={{ textAlign: 'center', py: 2 }}
                        >
                           No scans yet. Share your QR code to get started!
                        </Typography>
                     ) : (
                        <List sx={{ maxHeight: 200, overflow: 'auto' }}>
                           {scanHistory.slice(0, 5).map((scan) => (
                              <ListItem key={scan.id} sx={{ px: 0 }}>
                                 <ListItemAvatar>
                                    <Avatar
                                       src={scan.scannerPhotoURL}
                                       sx={{ width: 32, height: 32 }}
                                    >
                                       <PersonIcon />
                                    </Avatar>
                                 </ListItemAvatar>
                                 <ListItemText
                                    primary={scan.scannerName}
                                    secondary={`Scanned ${new Date(scan.scannedAt?.toDate()).toLocaleDateString()}`}
                                    sx={{ mr: 1 }}
                                 />
                                 {scan.signedUp && (
                                    <Chip
                                       label='Signed Up'
                                       size='small'
                                       color='success'
                                       sx={{ mr: 1 }}
                                    />
                                 )}
                              </ListItem>
                           ))}
                        </List>
                     )}
                  </CardContent>
               </Card>
            </Grid>

            {/* Signed Up Users Section */}
            <Grid item xs={12}>
               <Card sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                     <Typography variant='h6' gutterBottom>
                        Users Who Signed Up via Your Profile
                     </Typography>

                     {scanHistory.filter((scan) => scan.signedUp).length === 0 ? (
                        <Typography
                           variant='body2'
                           color='text.secondary'
                           sx={{ textAlign: 'center', py: 3 }}
                        >
                           No users have signed up via your profile yet.
                        </Typography>
                     ) : (
                        <List>
                           {scanHistory
                              .filter((scan) => scan.signedUp)
                              .map((scan) => (
                                 <ListItem
                                    key={scan.id}
                                    sx={{
                                       border: '1px solid #e0e0e0',
                                       borderRadius: 1,
                                       mb: 1,
                                       '&:hover': { bgcolor: 'action.hover' },
                                    }}
                                 >
                                    <ListItemAvatar>
                                       <Avatar
                                          src={scan.scannerPhotoURL}
                                          sx={{ width: 40, height: 40 }}
                                       >
                                          <PersonIcon />
                                       </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                       primary={scan.scannerName}
                                       secondaryTypographyProps={{ component: 'div' }}
                                       secondary={
                                          <Box>
                                             <Typography variant='body2' color='text.secondary'>
                                                {scan.scannerEmail}
                                             </Typography>
                                             <Typography variant='caption' color='text.secondary'>
                                                Signed up:{' '}
                                                {scan.signupDate
                                                   ? new Date(
                                                        scan.signupDate.toDate()
                                                     ).toLocaleDateString()
                                                   : 'Unknown'}
                                                {' • '}
                                                Via:{' '}
                                                {scan.source === 'profile_qr'
                                                   ? 'QR Code'
                                                   : scan.source === 'profile_link'
                                                     ? 'Profile Link'
                                                     : scan.source === 'business_card'
                                                       ? 'Business Card'
                                                       : 'Profile'}
                                             </Typography>
                                          </Box>
                                       }
                                    />
                                    <IconButton
                                       color='primary'
                                       onClick={() => handleStartChat(scan)}
                                       sx={{ ml: 1 }}
                                    >
                                       <ChatIcon />
                                    </IconButton>
                                 </ListItem>
                              ))}
                        </List>
                     )}
                  </CardContent>
               </Card>
            </Grid>
         </Grid>

         {/* Contact Modal */}
         <Dialog open={contactModalOpen} onClose={handleCloseContactModal} maxWidth='sm' fullWidth>
            <DialogTitle>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar src={selectedContact?.scannerPhotoURL} sx={{ width: 40, height: 40 }}>
                     {selectedContact?.scannerName?.charAt(0)?.toUpperCase()}
                  </Avatar>
                  <Box>
                     <Typography variant='h6'>
                        Send message to {selectedContact?.scannerName}
                     </Typography>
                     <Typography variant='body2' color='text.secondary'>
                        {selectedContact?.scannerEmail}
                     </Typography>
                  </Box>
               </Box>
            </DialogTitle>
            <DialogContent>
               <TextField
                  autoFocus
                  multiline
                  rows={4}
                  fullWidth
                  placeholder='Type your message...'
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  variant='outlined'
                  sx={{ mt: 1 }}
                  disabled={sendingMessage}
               />
               {sendingMessage && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                     <CircularProgress size={16} />
                     <Typography variant='body2' color='text.secondary'>
                        Sending your message...
                     </Typography>
                  </Box>
               )}
            </DialogContent>
            <DialogActions>
               <Button onClick={handleCloseContactModal} disabled={sendingMessage}>
                  Cancel
               </Button>
               <Button
                  onClick={handleSendMessage}
                  variant='contained'
                  disabled={!messageText.trim() || sendingMessage}
                  startIcon={sendingMessage ? <CircularProgress size={16} /> : <SendIcon />}
                  sx={{
                     bgcolor: '#00C4A7',
                     '&:hover': { bgcolor: '#017a7f' },
                     '&:disabled': { bgcolor: '#ccc' },
                  }}
               >
                  {sendingMessage ? 'Sending...' : 'Send Message'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* Share Dialog */}
         <Dialog
            open={shareDialogOpen}
            onClose={() => setShareDialogOpen(false)}
            maxWidth='sm'
            fullWidth
         >
            <DialogTitle>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  Share Your QR Code
                  <IconButton onClick={() => setShareDialogOpen(false)} size='small'>
                     <CloseIcon />
                  </IconButton>
               </Box>
            </DialogTitle>
            <DialogContent>
               <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                  Share this link with others so they can view your QR code and connect with you:
               </Typography>
               <TextField
                  fullWidth
                  value={qrUrl}
                  variant='outlined'
                  InputProps={{
                     readOnly: true,
                  }}
                  sx={{ mb: 2 }}
               />
            </DialogContent>
            <DialogActions>
               <Button onClick={handleCopyLink} variant='contained'>
                  Copy Link
               </Button>
            </DialogActions>
         </Dialog>
      </Box>
   );
};

export default ProfileQRCode;
